// Pure "Pace Bunny" math. On the solo track the player chases a friendly bunny
// that runs at their own personal-best pace. A pace is ms-per-correct-answer
// (totalMs / finishLine), which stays comparable across different finish-line
// lengths. Persistence/storage lives in src/lib/personalBest.ts; this file is
// browser-safe and node-free so it can be unit-tested.

/** Bunny pace before the player has any personal best (ms per correct answer). */
export const DEFAULT_PACE_MS_PER_CORRECT = 5500;

export interface BestPace {
  /** ms per correct answer — lower is faster. */
  msPerCorrect: number;
  /** The full-race time that set this best (ms), kept for display. */
  totalMs: number;
  /** Finish line of that run, for context. */
  finishLine: number;
}

/** Pace of a finished run, or null if it can't form a valid pace. */
export function paceOf(totalMs: number, finishLine: number): number | null {
  if (!(totalMs > 0) || !(finishLine > 0)) return null;
  return totalMs / finishLine;
}

/** Fold a finished run into the stored best, keeping the faster pace. Returns
 *  the (possibly unchanged) best and whether this run set a new record. */
export function foldBest(
  prev: BestPace | null,
  totalMs: number,
  finishLine: number,
): { best: BestPace | null; improved: boolean } {
  const ms = paceOf(totalMs, finishLine);
  if (ms === null) return { best: prev, improved: false };
  if (prev && prev.msPerCorrect <= ms) return { best: prev, improved: false };
  return { best: { msPerCorrect: ms, totalMs, finishLine }, improved: true };
}

/** ms-per-correct the bunny runs at — the player's best, or the default. */
export function bunnyPaceMs(best: BestPace | null): number {
  return best ? best.msPerCorrect : DEFAULT_PACE_MS_PER_CORRECT;
}

/** Bunny progress 0..1 at `nowMs` for a race that started at `startMs`. */
export function bunnyFraction(
  startMs: number,
  nowMs: number,
  finishLine: number,
  paceMs: number,
): number {
  if (startMs === 0 || !(finishLine > 0) || !(paceMs > 0)) return 0;
  return Math.min(1, Math.max(0, (nowMs - startMs) / (finishLine * paceMs)));
}
